import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { 
  ArrowRight, 
  Rocket, 
  Code, 
  Cloud,
  Smartphone,
  Layers,
  Puzzle,
  CheckCircle
} from 'lucide-react';
import { images } from '../data/mock';

const ProductDevelopmentPage = () => {
  const services = [
    {
      icon: Rocket,
      title: "Product Discovery & Strategy",
      description: "Define your product vision and create a roadmap for success.",
      items: [
        "Market research and competitive analysis",
        "User research and persona development",
        "Product vision and value proposition",
        "Feature prioritization frameworks"
      ]
    },
    {
      icon: Layers,
      title: "Roadmap Planning",
      description: "Create actionable product roadmaps aligned with business goals.",
      items: [
        "Strategic roadmap development",
        "Release planning and milestones",
        "Resource allocation planning",
        "Risk assessment and mitigation"
      ]
    },
    {
      icon: Cloud,
      title: "Cloud-Native SaaS Development",
      description: "Build scalable, resilient SaaS products on modern cloud platforms.",
      items: [
        "Cloud architecture design",
        "Microservices and containerization",
        "Serverless application development",
        "Multi-tenant SaaS platforms"
      ]
    },
    {
      icon: Smartphone,
      title: "Web & Mobile Application Development",
      description: "Create engaging digital experiences across all platforms.",
      items: [
        "Progressive web applications",
        "Native iOS and Android development",
        "Cross-platform mobile solutions",
        "Responsive design and UX optimization"
      ]
    },
    {
      icon: Code,
      title: "AI-Powered Products & Optimization",
      description: "Embed intelligence into your products for enhanced user experiences.",
      items: [
        "AI feature integration",
        "Recommendation engines",
        "Predictive analytics dashboards",
        "Personalization and optimization"
      ]
    },
    {
      icon: Puzzle,
      title: "Custom Engineering & Integrations",
      description: "Build custom solutions and seamlessly integrate with existing systems.",
      items: [
        "Custom software development",
        "API design and development",
        "Third-party system integrations",
        "Legacy system modernization"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      <SEO 
        title="Product Development Consulting"
        description="Transform your product vision into reality with TekClap's product development consulting. Product strategy, cloud-native SaaS, web and mobile apps, and AI-powered solutions."
        keywords="product development consulting, SaaS development, web application development, mobile app development, cloud-native development, product strategy"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#FFF4E6] to-[#FAFAF8]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
                Product Development
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1A1A1A] mb-6">
                Product Development Consulting That Delivers Results
              </h1>
              <p className="text-lg text-[#6B7280] leading-relaxed mb-8">
                From product discovery to launch and beyond, TekClap's product development consulting 
                helps you build products that users love. We combine strategic thinking with technical 
                excellence to deliver cloud-native, AI-powered solutions.
              </p>
              <Link to="/contact">
                <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base">
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src={images.productDev} 
                alt="Product Development" 
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FFF4E6] rounded-full flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-[#F58B23]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#1A1A1A]">End-to-End</div>
                    <div className="text-sm text-[#6B7280]">Product Lifecycle</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-6">
              Full-Spectrum Product Development Services
            </h2>
            <p className="text-lg text-[#6B7280]">
              We support every phase of your product journey, from initial concept 
              through development, launch, and continuous improvement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#FFF4E6] rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#F58B23]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#6B7280] mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-[#374151]">
                      <CheckCircle className="w-5 h-5 text-[#F58B23] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
            Ready to Build Your Next Product?
          </h2>
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            Let&apos;s discuss how our product development consulting can help you bring 
            your vision to life and create products that drive business growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base">
                Schedule Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/client-impact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] font-medium px-8 py-6 text-base">
                View Client Impact
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-8">
            Explore Related Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/technology-consulting" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Technology Consulting →
              </h3>
              <p className="text-sm text-[#6B7280]">
                Digital transformation, technology strategy, and architecture design.
              </p>
            </Link>
            <Link to="/ai-consulting" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                AI Consulting →
              </h3>
              <p className="text-sm text-[#6B7280]">
                AI strategy, intelligent automation, and embedded AI systems.
              </p>
            </Link>
            <Link to="/workforce-solutions" className="group p-6 bg-[#FAFAF8] rounded-xl hover:shadow-md transition-all">
              <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                Workforce Solutions →
              </h3>
              <p className="text-sm text-[#6B7280]">
                Product and engineering talent, team augmentation, and staffing.
              </p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDevelopmentPage;
